import { useEffect, useMemo, useState } from 'react';
import { BarChart3, TrendingUp } from 'lucide-react';
import { useAuthStore, useSettingsStore } from '../store';
import { api } from '../utils/api';
import ScoreTrendChart, { type TrendDataPoint } from '../components/ScoreTrendChart';
import MyVocab from './MyVocab';

type TrendResponse = Awaited<ReturnType<typeof api.getUserExamTrends>>;
type Version = TrendResponse['events'][number]['version'];

const CSCA_NAMES: Record<number, string> = { 1: '中文文科', 2: '中文理科', 3: '数学', 4: '物理', 5: '化学' };
const VERSION_ORDER: Version[] = ['2.0', '3.0', 'CSCA'];
const SCORE_KEYS = ['listening', 'reading', 'writing', 'total'] as const;
const SCORE_LABELS: Record<(typeof SCORE_KEYS)[number], string> = { listening: '听力', reading: '阅读', writing: '写作', total: '总分' };

export default function ExamStats() {
  const { token } = useAuthStore();
  const { systemLanguage } = useSettingsStore();
  const [response, setResponse] = useState<TrendResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);

  const load = () => {
    if (!token) return;
    setLoading(true);
    setError(false);
    api.getUserExamTrends(token).then(setResponse).catch(() => setError(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const events = response?.events || [];
  const versionWithEvents = VERSION_ORDER.filter(version => events.some(event => event.version === version));
  // The HSK curriculum is assigned as 2.0 or 3.0. A legacy account with both
  // receives its most recently used HSK curriculum, plus CSCA if applicable.
  const hskVersions = versionWithEvents.filter(version => version !== 'CSCA');
  const assignedHsk = hskVersions.length <= 1 ? hskVersions[0] : [...events]
    .filter(event => event.version !== 'CSCA')
    .sort((a, b) => b.completedAt.localeCompare(a.completedAt))[0]?.version;
  const versions = [assignedHsk, versionWithEvents.includes('CSCA') ? 'CSCA' as const : undefined]
    .filter((version): version is Version => Boolean(version));
  const vocabVersions = useMemo<Version[]>(() => {
    if (!response) return [];
    const restrictions = response.restrictions;
    const hsk = assignedHsk || (!restrictions.hsk2Restricted && restrictions.hsk2Levels.length ? '2.0'
      : !restrictions.hsk3Restricted && restrictions.hsk3Levels.length ? '3.0' : undefined);
    return [hsk, !restrictions.cscaRestricted && restrictions.cscaLevels.length ? 'CSCA' : undefined]
      .filter((item): item is Version => Boolean(item));
  }, [response, assignedHsk]);
  const version = selectedVersion && versions.includes(selectedVersion) ? selectedVersion : versions[0];
  const levels = [...new Set(events.filter(event => event.version === version).map(event => event.level))].sort((a, b) => a - b);
  const defaultLevel = version === 'CSCA' && levels.includes(3) ? 3 : levels[levels.length - 1];
  const level = selectedLevel != null && levels.includes(selectedLevel) ? selectedLevel : defaultLevel;
  const scopedEvents = useMemo(() => events.filter(event => event.version === version && event.level === level)
    .sort((a, b) => a.completedAt.localeCompare(b.completedAt)), [events, version, level]);
  const trendData = useMemo<TrendDataPoint[]>(() => scopedEvents.flatMap(event =>
    SCORE_KEYS.filter(key => event.scores[key] != null && (version !== 'CSCA' || key === 'total'))
      .map(key => ({
        eventId: event.id, completedAt: event.completedAt, percentage: event.scores[key] as number,
        part: key === 'total' ? 'simulation' : key,
        name: `${new Date(event.completedAt).toLocaleDateString(systemLanguage === 'zh' ? 'zh-CN' : systemLanguage === 'id' ? 'id-ID' : 'en-US')} · ${SCORE_LABELS[key]}`,
      }))), [scopedEvents, systemLanguage, version]);
  const latestTotal = [...scopedEvents].reverse().find(event => event.scores.total != null)?.scores.total;

  const heading = systemLanguage === 'id' ? 'Tren Nilai Ujian' : systemLanguage === 'en' ? 'Exam Score Trends' : '考试成绩趋势';
  const subtitle = systemLanguage === 'id' ? 'Simulasi dan mock menurut kurikulum serta level.'
    : systemLanguage === 'en' ? 'Simulation and mock results by curriculum and level.' : '按学习体系与等级查看模拟考试和 Mock 成绩。';
  const noData = systemLanguage === 'id' ? 'Belum ada hasil simulasi atau mock.'
    : systemLanguage === 'en' ? 'No simulation or mock results yet.' : '暂无模拟考试或 Mock 成绩。';
  const levelName = (value: number) => version === 'CSCA' ? CSCA_NAMES[value] || `CSCA ${value}` : `HSK ${value} 级`;

  return <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
    <header className="space-y-2">
      <div className="flex items-center gap-3 text-indigo-900"><BarChart3 aria-hidden="true" className="w-8 h-8" /><h1 className="text-3xl font-bold">{heading}</h1></div>
      <p className="text-slate-600">{subtitle}</p>
    </header>

    {loading && !response ? <div className="rounded-2xl bg-white border border-slate-200 p-8 text-slate-500" role="status">{systemLanguage === 'zh' ? '正在读取成绩…' : 'Loading results…'}</div>
      : error ? <div className="rounded-2xl bg-white border border-red-100 p-8 text-red-700" role="alert">
        {systemLanguage === 'zh' ? '成绩读取失败。' : 'Could not load results.'} <button className="underline" onClick={load}>{systemLanguage === 'zh' ? '重试' : 'Retry'}</button>
      </div>
      : versions.length === 0 ? <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">{noData}</div>
      : <>
        <nav aria-label="考试体系" className="flex flex-wrap gap-2">
          {versions.map(item => <button type="button" key={item} aria-current={item === version ? 'page' : undefined}
            onClick={() => { setSelectedVersion(item); setSelectedLevel(null); }}
            className={`rounded-xl px-5 py-2.5 font-semibold transition-colors ${item === version ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200 text-indigo-800 hover:bg-indigo-50'}`}>
            {item === 'CSCA' ? 'CSCA' : `HSK ${item}`}
          </button>)}
        </nav>
        <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-7 space-y-6">
          <div className="flex flex-wrap gap-2" role="group" aria-label="等级或科目">
            {levels.map(item => <button type="button" key={item} aria-pressed={item === level}
              onClick={() => setSelectedLevel(item)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${item === level ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-500' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              {levelName(item)}
            </button>)}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-5">
            <div><div className="flex items-center gap-2 text-indigo-900"><TrendingUp aria-hidden="true" className="w-5 h-5" /><h2 className="text-xl font-bold">{levelName(level)} · {systemLanguage === 'zh' ? '成绩趋势' : 'Score trend'}</h2></div>
              <p className="text-sm text-slate-500 mt-1">{scopedEvents.length} {systemLanguage === 'zh' ? '次模拟／Mock 记录' : 'simulation / mock results'}</p></div>
            {latestTotal != null && <div className="rounded-xl bg-indigo-50 px-5 py-3 text-right"><div className="text-xs text-indigo-700">{systemLanguage === 'zh' ? '最近一次总分' : 'Latest total'}</div><strong className="text-2xl text-indigo-900">{latestTotal}%</strong></div>}
          </div>
          <ScoreTrendChart data={trendData} family={version === 'CSCA' ? 'CSCA' : 'HSK'} />
          {version !== 'CSCA' && <p className="text-xs text-slate-500">{systemLanguage === 'zh' ? '听力、阅读、写作包含分项 Mock 和完整模拟考试的分项成绩；总分只来自完整模拟考试。图表显示最近 20 次考试。' : 'Section trends include mock sections and full simulations. Total is shown for full simulations only. The chart shows the latest 20 exams.'}</p>}
        </section>
      </>}
    {response && !error && <MyVocab embedded initialRestrictions={response.restrictions}
      visibleVersions={vocabVersions}
      readOnlyPreview={import.meta.env.VITE_READ_ONLY_PREVIEW === 'true'} />}
  </main>;
}
